import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { parentApi, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, Button, Input, Modal, Pill } from "../../components/ui";
import ConfirmDialog from "../../components/ConfirmDialog";
import Avatar, { AVATAR_OPTIONS } from "../../components/Avatar";
import Icon from "../../components/Icon";

const COLORS = ["#7C3AED", "#FB7185", "#3B82F6", "#FBBF24", "#A855F7", "#F97316", "#06B6D4", "#EC4899"];

export default function Family() {
  const { family, setFamily, showEditControls, isOwner, demoGuard } = useAuth();
  const [children, setChildren] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [confirmMember, setConfirmMember] = useState(null);
  const [form, setForm] = useState({ name: "", avatar: "mouse", color: "#7C3AED", pin: "", weekly_goal: 100 });

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([parentApi.get("/children"), parentApi.get("/family/members")]);
      setChildren(c.data.children);
      setMembers(m.data.members);
    }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", avatar: "mouse", color: "#7C3AED", pin: "", weekly_goal: 100 }); setModal(true); };
  const openEdit = (k) => { setEditing(k); setForm({ name: k.name, avatar: k.avatar, color: k.color, pin: "", weekly_goal: k.stats.weekly_goal }); setModal(true); };
  const save = async () => {
    if (demoGuard()) return;
    if (!form.name.trim()) { toast.error("Enter a name."); return; }
    const payload = { ...form, pin: form.pin ? form.pin : null };
    try {
      if (editing) await parentApi.put(`/children/${editing.id}`, payload);
      else await parentApi.post("/children", payload);
      toast.success(editing ? "Kid updated" : `${form.name} added! 🌱`);
      setModal(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { if (demoGuard()) return; await parentApi.delete(`/children/${id}`); toast.success("Kid removed"); load(); };

  const copyCode = () => { navigator.clipboard?.writeText(family?.access_code || ""); toast.success("Family code copied!"); };
  const regen = async () => {
    if (demoGuard()) return;
    const { data } = await parentApi.post("/family/regenerate-code");
    setFamily((f) => ({ ...f, access_code: data.access_code }));
    toast.success("New family code generated");
  };
  const setPerm = async (m, value) => {
    if (demoGuard()) return;
    try {
      await parentApi.put(`/family/members/${m.id}/permission`, { can_edit: value });
      setMembers((list) => list.map((x) => (x.id === m.id ? { ...x, can_edit: value } : x)));
      toast.success(value ? `${m.name} can now make changes` : `${m.name} is now view-only`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeMember = async (m) => {
    if (demoGuard()) return;
    try {
      await parentApi.delete(`/family/members/${m.id}`);
      setMembers((list) => list.filter((x) => x.id !== m.id));
      toast.success(`${m.name} removed from the family`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="grid place-items-center py-20"><div className="w-10 h-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="family-page">
      <div><h1 className="font-heading font-black text-3xl text-ink">Family</h1><p className="text-ink-soft font-medium">Manage kids and your kids' door code.</p></div>

      <Card kid className="p-6 grain">
        <div className="flex items-center gap-2 mb-1"><Icon name="key-round" className="text-primary" width={20} height={20} /><span className="font-heading font-extrabold text-ink">Family code</span></div>
        <p className="text-sm text-ink-soft font-medium mb-4">Kids type this on the <b>Kids door</b> to play. Grown-ups can enter it when signing up to join your family.</p>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-white rounded-2xl border-2 border-primary/20 py-4 text-center font-heading font-black text-3xl tracking-[0.3em] text-primary-dark" data-testid="family-code">{family?.access_code}</div>
          <button onClick={copyCode} className="w-12 h-12 rounded-2xl bg-primary text-white grid place-items-center shadow-claybtn" data-testid="copy-code" aria-label="Copy"><Icon name="copy" width={20} height={20} /></button>
        </div>
        {showEditControls && (
          <button onClick={() => { if (demoGuard()) return; setConfirmRegen(true); }} className="mt-3 text-sm font-bold text-ink-soft hover:text-primary flex items-center gap-1" data-testid="regen-code"><Icon name="refresh-cw" width={14} height={14} /> Change code</button>
        )}
      </Card>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-extrabold text-xl text-ink flex items-center gap-2"><Icon name="user-cog" className="text-primary" width={20} height={20} /> Grown-ups ({members.length})</h2>
        </div>
        <div className="space-y-3">
          {members.map((m) => (
            <Card key={m.id} className="p-4 flex items-center gap-3" data-testid="member-card">
              <div className="w-11 h-11 rounded-2xl bg-primary/10 grid place-items-center text-primary shrink-0"><Icon name={m.is_owner ? "crown" : "user"} width={22} height={22} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-extrabold text-ink truncate">{m.name}{m.is_self && " (you)"}</div>
                <div className="text-sm text-ink-soft font-medium truncate">{m.email}</div>
              </div>
              {m.is_owner ? (
                <Pill tone="primary"><Icon name="crown" width={12} height={12} /> Owner</Pill>
              ) : isOwner ? (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setPerm(m, !m.can_edit)} data-testid={`toggle-perm-${m.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${m.can_edit ? "bg-primary text-white" : "bg-slate-100 text-ink-soft"}`}>
                    {m.can_edit ? "Can edit" : "View-only"}
                  </button>
                  <button onClick={() => { if (demoGuard()) return; setConfirmMember(m); }} data-testid={`remove-member-${m.id}`} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-coral/20 hover:text-coral text-ink-soft grid place-items-center" aria-label="Remove"><Icon name="user-minus" width={15} height={15} /></button>
                </div>
              ) : (
                <Pill tone={m.can_edit ? "primary" : "slate"}>{m.can_edit ? "Can edit" : "View-only"}</Pill>
              )}
            </Card>
          ))}
        </div>
        {isOwner && (
          <p className="text-[12px] text-ink-muted font-semibold mt-2 flex items-center gap-1.5">
            <Icon name="info" width={13} height={13} /> Invite another grown-up: share your family code — they enter it when creating an account.
          </p>
        )}
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-heading font-extrabold text-xl text-ink">Kids ({children.length})</h2>
        {showEditControls && <Button onClick={openNew} size="sm" data-testid="add-kid-btn"><Icon name="plus" width={16} height={16} /> Add kid</Button>}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {children.map((k) => (
          <Card key={k.id} className="p-5" data-testid="kid-card">
            <div className="flex items-center gap-3">
              <Avatar avatar={k.avatar} color={k.color} size={52} />
              <div className="flex-1 min-w-0">
                <div className="font-heading font-extrabold text-lg text-ink truncate">{k.name}</div>
                <div className="flex gap-2 mt-1">{k.has_pin ? <Pill tone="primary"><Icon name="lock" width={12} height={12} /> PIN set</Pill> : <Pill tone="slate">No PIN</Pill>}<Pill tone="sunny"><Icon name="target" width={12} height={12} /> Goal {k.stats.weekly_goal}</Pill></div>
              </div>
              <div className="flex flex-col gap-1">
                {showEditControls && <button onClick={() => openEdit(k)} data-testid={`edit-kid-${k.id}`} className="w-9 h-9 rounded-full bg-slate-100 text-ink-soft grid place-items-center" aria-label="Edit"><Icon name="pencil" width={16} height={16} /></button>}
                {showEditControls && <button onClick={() => { if (demoGuard()) return; setConfirmDel(k); }} data-testid={`delete-kid-${k.id}`} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-coral/20 hover:text-coral text-ink-soft grid place-items-center" aria-label="Delete"><Icon name="trash-2" width={16} height={16} /></button>}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit kid" : "Add a kid"} testid="kid-modal">
        <div className="space-y-4">
          <div className="flex justify-center"><Avatar avatar={form.avatar} color={form.color} size={72} /></div>
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mia" data-testid="kid-name" />
          <div>
            <span className="block mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">Avatar</span>
            <div className="grid grid-cols-5 gap-2">
              {AVATAR_OPTIONS.map((a) => (
                <button key={a} onClick={() => setForm({ ...form, avatar: a })} className={`aspect-square rounded-2xl grid place-items-center border-2 ${form.avatar === a ? "border-primary bg-primary/5" : "border-slate-100"}`} data-testid={`avatar-${a}`}><Avatar avatar={a} color={form.color} size={38} ring={false} /></button>
              ))}
            </div>
          </div>
          <div>
            <span className="block mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">Color</span>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (<button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-9 h-9 rounded-full border-4 ${form.color === c ? "border-ink" : "border-white"} shadow-sm`} style={{ background: c }} data-testid={`color-${c}`} aria-label={c} />))}
            </div>
          </div>
          <Input label={editing ? "PIN (leave blank to keep)" : "PIN (optional, 4 digits)"} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="1234" inputMode="numeric" data-testid="kid-pin" />
          <div>
            <span className="block mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">Weekly goal (coins)</span>
            <input type="number" min="0" value={form.weekly_goal} onChange={(e) => setForm({ ...form, weekly_goal: parseInt(e.target.value || "0", 10) })} className="w-full rounded-2xl border-2 border-slate-200 px-4 py-3 font-bold text-ink focus-ring" data-testid="kid-goal" />
          </div>
          <Button onClick={save} size="lg" className="w-full" data-testid="save-kid">{editing ? "Save changes" : "Add kid"}</Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={async () => { await remove(confirmDel.id); setConfirmDel(null); }}
        title={confirmDel ? `Remove ${confirmDel.name}?` : "Remove kid?"}
        message="This deletes their chores, coins and history. This can't be undone."
        confirmLabel="Remove kid"
        testid="delete-kid-confirm"
      />

      <ConfirmDialog
        open={confirmRegen}
        onClose={() => setConfirmRegen(false)}
        onConfirm={async () => { await regen(); setConfirmRegen(false); }}
        title="Change your family code?"
        message="Your current code will stop working right away. Kids and grown-ups will need the NEW code to sign in or join. Only change it if you have to."
        confirmLabel="Yes, change it"
        cancelLabel="Keep current code"
        icon="refresh-cw"
        testid="regen-code-confirm"
      />

      <ConfirmDialog
        open={!!confirmMember}
        onClose={() => setConfirmMember(null)}
        onConfirm={async () => { await removeMember(confirmMember); setConfirmMember(null); }}
        title={confirmMember ? `Remove ${confirmMember.name}?` : "Remove grown-up?"}
        message="They'll lose access to your family. They can rejoin later with your family code if you invite them again."
        confirmLabel="Remove"
        testid="remove-member-confirm"
      />
    </div>
  );
}
